import React, { useEffect, useCallback, useRef } from 'react'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'

const TOAST_DURATION = 10000

export type NotificationToastVariant = 'success' | 'error' | 'warning'

interface NotificationToastProps {
  message: React.ReactNode
  variant?: NotificationToastVariant
  onUndo?: (() => void) | null
  undoLabel?: string
  onDismiss: () => void
  duration?: number
}

function NotificationToast ({
  message,
  variant = 'success',
  onUndo = null,
  undoLabel = 'undo',
  onDismiss,
  duration = TOAST_DURATION,
}: NotificationToastProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss
  // Guard against dismiss/undo race: whichever fires first wins
  const dismissedRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    dismissedRef.current = false
    timerRef.current = setTimeout(() => {
      if (!dismissedRef.current) {
        dismissedRef.current = true
        onDismissRef.current()
      }
    }, duration)
    return clearTimer
  }, [duration, clearTimer])

  const handleUndo = useCallback(() => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    clearTimer()
    if (onUndo) onUndo()
  }, [onUndo, clearTimer])

  let iconType = ICON_TYPES.ALERT
  if (variant === 'success') iconType = ICON_TYPES.CHECK
  else if (variant === 'error') iconType = ICON_TYPES.CLOSE

  return (
    <div
      className={`notification-toast notification-toast--${variant}`}
      role="alert"
    >
      <IconV2 type={iconType} />
      <span className="notification-toast__message">{message}</span>
      {onUndo ? (
        <button
          type="button"
          className="notification-toast__undo"
          onClick={handleUndo}
        >
          {undoLabel}
        </button>
      ) : null}
    </div>
  )
}

export default NotificationToast
